import { API_CONFIG, buildApiUrl, getAuthHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

// Types for Employee API
export interface Employee {
  id: number;
  name: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  createdDate: number;
  employeeId?: string;
  designation?: string;
}

export interface CreateEmployeeRequest {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  employeeId?: string;
  designation?: string;
}

export interface UpdateEmployeeRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  employeeId?: string;
  designation?: string;
}

export interface EmployeeImportSummary {
  totalRows: number;
  imported: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export interface PaginatedEmployeesResponse {
  data: Employee[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

// Employee API calls
export const employeeApi = {
  // Get all employees with pagination
  getEmployees: async (
    params: PaginationParams = {},
    token: string
  ): Promise<PaginatedEmployeesResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.EMPLOYEES.BASE)}?${searchParams.toString()}`;
    
    return apiRequest<PaginatedEmployeesResponse>(
      url,
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Get employee by ID
  getEmployee: async (id: number, token: string): Promise<Employee> => {
    return apiRequest<Employee>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EMPLOYEES.BY_ID(id)),
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Create new employee
  createEmployee: async (data: CreateEmployeeRequest, token: string): Promise<Employee> => {
    return apiRequest<Employee>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EMPLOYEES.BASE),
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Update employee
  updateEmployee: async (
    id: number,
    data: UpdateEmployeeRequest,
    token: string
  ): Promise<Employee> => {
    return apiRequest<Employee>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EMPLOYEES.BY_ID(id)),
      {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Delete employee
  deleteEmployee: async (id: number, token: string): Promise<void> => {
    return apiRequest<void>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EMPLOYEES.BY_ID(id)),
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Bulk-import employees from a CSV file (admin only)
  importCsv: async (file: File, token: string): Promise<EmployeeImportSummary> => {
    const formData = new FormData();
    formData.append('file', file);
    // Deliberately no Content-Type header - the browser sets the multipart boundary.
    return apiRequest<EmployeeImportSummary>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EMPLOYEES.IMPORT),
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );
  },
};
